import { Button, KIND, SHAPE, SIZE } from 'baseui/button'
import { SizeButton } from '@/constants/sizeButton'
import Tooltip from '@/components/Tooltip'
import { PLACEMENT } from 'baseui/popover'
import { StyleObject } from 'styletron-standard'
import { lightTheme } from '@/customTheme'
import { expandBorder, expandBox, expandRadius, normalizeShorthandStyle } from '@/utils/baseWebStyleLonghand'

export interface PropsButtonCustom {
  // size?: SIZE['compact'] | SIZE['default'] | SIZE['large'] | SIZE['mini']
  kind?: KIND[keyof KIND]
  shape?: SHAPE[keyof SHAPE]
  onClick?: any //function hanlde click button
  type: SizeButton
  tooltip?: {
    content: string | React.ReactNode
    placement?: PLACEMENT[keyof PLACEMENT]
  } // give content if want display tooltip
  style?: StyleObject //custom style for button
  children: JSX.Element | JSX.Element[] | string
  startEnhancer?: JSX.Element
  endEnhancer?: JSX.Element
  transparent?: boolean
  disabled?: boolean
  selected?: boolean
  isLoading?: boolean
}
function ButtonCustom({
  kind,
  shape,
  onClick,
  type,
  tooltip,
  style,
  children,
  startEnhancer,
  endEnhancer,
  transparent,
  disabled,
  selected,
  isLoading
}: PropsButtonCustom) {
  const sizeCustom =
    type === SizeButton.SMALL
      ? '32px'
      : type === SizeButton.STANDARD
      ? '40px'
      : type === SizeButton.LARGE
      ? '48px'
      : type === SizeButton.EXTRA_SMALL
      ? '24px'
      : '32px'
  const isPill = kind === KIND.primary || kind === KIND.secondary
  const {
    useRoundedCorners: _ignoredUseRoundedCorners,
    ...safeStyle
  } = (style || {}) as StyleObject & { useRoundedCorners?: unknown }

  return (
    <Tooltip info={tooltip}>
      <Button
        disabled={disabled || isLoading}
        kind={kind ?? 'minimal'}
        shape={shape}
        onClick={onClick ? onClick : null}
        startEnhancer={startEnhancer}
        endEnhancer={endEnhancer}
        isLoading={isLoading}
        overrides={{
          BaseButton: {
            style: ({ $theme }) => {
              const borderStyles =
                kind === KIND.tertiary
                  ? expandBorder(selected ? '2px' : '1px', 'solid', selected ? $theme.colors.pink : $theme.colors.grayScale100)
                  : expandBorder('0', 'none', 'transparent')
              return {
              ...$theme,
              ...lightTheme.typography.Small14Semibold,
              ...borderStyles,
              width: shape === SHAPE.square ? sizeCustom : '100%',
              height: sizeCustom,
              background: (kind !== KIND.tertiary && selected)
              ? lightTheme.colors.grayScale150
              : transparent ? lightTheme.colors.grayScale50 : kind === 'primary' ? disabled ? `${lightTheme.colors.grayScale50}` : lightTheme.colors.primary : $theme.background,
              ...(isPill ? { ...expandRadius('100px'), ...expandBox('padding', '0px 16px') } : {}),
              outline: 'none',
              ':hover': {
                ...borderStyles,
                background: transparent ? lightTheme.colors.grayScale100 : $theme.background,
              },
              ':active': {
                background: transparent ? lightTheme.colors.grayScale150 : $theme.background,
              },
              color: disabled ? "#ccc" : kind !== KIND.primary ? lightTheme.colors.contentPrimary : '#fff',
              ...normalizeShorthandStyle(safeStyle),
              }
            },
          },
          StartEnhancer: {
            style: ({ $theme }) => ({
              ...$theme,
              marginRight: '8px',
              fill: disabled ? "#ccc !important" : lightTheme.colors.contentPrimary,
            }),
          },
          EndEnhancer: {
            style: ({ $theme }) => ({
              ...$theme,
              marginLeft: 'auto',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }),
          },
          LoadingSpinner: {
            style: ({ $theme }) => {
              const color = kind === KIND.primary ? '#fff' : lightTheme.colors.contentPrimary
              return ({
              ...$theme,
              borderTopColor: color,
              borderRightColor: color,
              borderBottomColor: color,
              borderLeftColor: 'transparent',
              width: '24px',
              height: '24px',
            })
            },
          }
        }}
      >
        {children}
      </Button>
    </Tooltip>
  )
}

export default ButtonCustom
